/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-vi — wraps ResultsLoadFailed with
 * a live retry hook + outfit-id-aware support email.
 *
 * SOCIAL-SHARE-FACEBOOK-1 — additionally reads `lookbook_error` from the
 * outfits row so the wrapped surface can swap to the Facebook-Story
 * "Stories can't be shared" copy when that specific policy reject fires.
 * Read is one-shot on mount; the failed-load surface is a terminal state.
 */
import { useEffect, useState } from "react";
import ResultsLoadFailed from "@/components/results/ResultsLoadFailed";
import { useRetryAnalysis } from "@/hooks/useRetryAnalysis";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  outfitId: string | null;
  isGuest: boolean;
  onBack: () => void;
  onRetryDispatched?: (newOutfitId: string) => void;
}

export default function ResultsLoadFailedSlot({
  outfitId,
  onBack,
  onRetryDispatched,
}: Props) {
  const { retry, canRetry, status } = useRetryAnalysis(outfitId);
  const [lookbookError, setLookbookError] = useState<string | null>(null);

  useEffect(() => {
    if (!outfitId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("outfits")
        .select("lookbook_error")
        .eq("id", outfitId)
        .maybeSingle();
      if (cancelled) return;
      setLookbookError((data?.lookbook_error as string | null) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [outfitId]);

  return (
    <ResultsLoadFailed
      issueNumber={1}
      outfitId={outfitId}
      onBack={onBack}
      canRetry={canRetry}
      retrying={status === "retrying"}
      lookbookError={lookbookError}
      onRetry={async () => {
        const res = await retry("load_failed");
        if (res?.newOutfitId && onRetryDispatched) {
          onRetryDispatched(res.newOutfitId);
        }
      }}
    />
  );
}
